// The five hold-fire looks of mockup round 063 (R1439, `ui-map-interface.md`
// §6.1.7): card 02 SAFETY LOCK, the AUTO look, card 04 SAFED GUNS, card 07
// RESTRICTED LINE, card 08 CLAMP and card 10 HATCH SHIMMER.
//
// One entry per look id of `labels.toml hold_fire_styles`. `RECIPES` holds
// the built cards and `PENDING` the ones still to build; a pending look draws
// the AUTO look, and `holdfire-styles.test.ts` pins that the two lists cover
// the whole list once.
//
// Every FIGURE is in the look's row of `data/presentation/holdfire.toml`. A
// constant here is a PROPORTION of a figure the row carries: the quotient
// says what the card wrote, and it holds at any figure (R1120).

import {
  TAU, arcOf, frac, line, ring, spoke, wave,
  type HoldFireCtx, type HoldFireRecipe,
} from './holdfire-recipe-kit';
import { HOLD_FIRE_LIMITS } from '../gen/presentation';

// ---------------------------------------------------------------------------
// Card 02, SAFETY LOCK
// ---------------------------------------------------------------------------

/** The share of one quarter that an arc covers: the card's 0.94 radians of
 *  a quarter turn. The rest of the quarter is the gap with its tooth. */
const LOCK_ARC_SHARE = 0.94 / (Math.PI / 2);

/**
 * Four arcs at r24 with four gaps, and a tooth that points in at the middle
 * of each gap, like the ring of a combination lock. The card draws r21; the
 * row moves the lock out, clear of the r19 ring of the anchoring look
 * `ring-teeth` (R1439). The lock turns one lap
 * in `period_sec`, clockwise, on the shared clock.
 */
function safetyLock(c: HoldFireCtx): void {
  const r = c.row;
  const n = Math.max(1, r.count);
  const step = TAU / n;
  const sweep = step * LOCK_ARC_SHARE;
  const rot = (c.t * TAU) / r.periodSec;
  for (let k = 0; k < n; k++) {
    const a0 = rot + k * step;
    arcOf(c, r.rPx, r.widthPx, a0, sweep, r.alpha);
    const g = a0 + sweep + (step - sweep) / 2;
    spoke(c, g, r.rPx, r.r2Px, r.width2Px, r.alpha2);
  }
}

// ---------------------------------------------------------------------------
// Card 04, SAFED GUNS
// ---------------------------------------------------------------------------

/** How far the slash turns off the barrel: the card's 45 degrees. */
const SLASH_TURN = Math.PI / 4;

/**
 * Gun ticks that orbit the hull, spread evenly: a radial barrel with a slash
 * across its middle, a gun that is safed. One lap in `period_sec`.
 */
function safedGuns(c: HoldFireCtx): void {
  const r = c.row;
  const n = Math.max(1, r.count);
  const rot = (c.t * TAU) / r.periodSec;
  for (let k = 0; k < n; k++) {
    const a = rot + (k * TAU) / n;
    spoke(c, a, r.rPx - r.lengthPx, r.rPx + r.lengthPx, r.widthPx, r.alpha);
    const cx = c.x + Math.cos(a) * r.rPx, cy = c.y + Math.sin(a) * r.rPx;
    const d = a + SLASH_TURN;
    const dx = Math.cos(d) * r.length2Px, dy = Math.sin(d) * r.length2Px;
    line(c, cx - dx, cy - dy, cx + dx, cy + dy, r.width2Px, r.alpha2);
  }
}

// ---------------------------------------------------------------------------
// Card 08, CLAMP
// ---------------------------------------------------------------------------

/** Where the breath of the clamp starts: the card's `wave(t, 5, 0.25)`, so
 *  the clamp stands wide open at t = 0. */
const CLAMP_PHASE = 0.25;

/**
 * Two chevrons at three and nine o'clock that point in at the hull. They
 * close to `r_px` and open by `length_px`, once in `period_sec`. They stay off
 * the top and the bottom, so they never touch the bars or the badge.
 */
function clamp(c: HoldFireCtx): void {
  const r = c.row;
  const d = r.rPx + r.lengthPx * wave(c.t, r.periodSec, CLAMP_PHASE);
  const arm = r.length2Px;
  for (let s = -1; s <= 1; s += 2) {
    const px = c.x + s * d, ox = c.x + s * (d + arm);
    line(c, ox, c.y - arm, px, c.y, r.widthPx, r.alpha);
    line(c, px, c.y, ox, c.y + arm, r.widthPx, r.alpha);
  }
}

// ---------------------------------------------------------------------------
// Card 07, RESTRICTED LINE (the chart)
// ---------------------------------------------------------------------------

/** The share of its step that one dash covers: the card's half dash of
 *  `TAU / N * 0.3`, so the dash is 0.6 of the step and the gap 0.4. */
const DASH_SHARE = 0.6;

/**
 * The T line of a restricted area on a printed chart: dashes at r23 with a
 * tick on the inside of each, turning one lap in `period_sec`. A wave of
 * alpha runs round the dashes once in `period2_sec`. The selected hull adds
 * the chart legend "guns housed" under the ring.
 */
function restrictedLine(c: HoldFireCtx): void {
  const r = c.row;
  const n = Math.max(1, r.count);
  const step = TAU / n;
  const sweep = step * DASH_SHARE;
  const rot = (c.t * TAU) / r.periodSec;
  const run = (c.t * TAU) / r.period2Sec;
  for (let k = 0; k < n; k++) {
    const a = rot + k * step;
    const al = r.alpha + r.alpha2 * (0.5 + 0.5 * Math.cos(k * step - run));
    arcOf(c, r.rPx, r.widthPx, a - sweep / 2, sweep, al);
    spoke(c, a, r.rPx, r.r2Px, r.width2Px, al);
  }
  if (c.selected && r.textPx > 0) {
    c.legend(c.x + r.xPx, c.y + r.yPx, r.textPx, HOLD_FIRE_LIMITS.ink, r.alpha3);
  }
}

// ---------------------------------------------------------------------------
// Card 10, HATCH SHIMMER
// ---------------------------------------------------------------------------

/** The turn of the hatch: the card's 45 degrees. */
const HATCH_TURN = Math.PI / 4;
const HATCH_COS = Math.cos(HATCH_TURN), HATCH_SIN = Math.sin(HATCH_TURN);

/**
 * A disc of water under the hull, hatched at 45 degrees and faint, with a
 * rim. A band of light crosses the hatch once in `period_sec`: it runs from
 * `r2_px` on one side of the centre to `r2_px` on the other, so it stands
 * off the disc for half of the cycle.
 *
 * The card clips each stroke to the disc. Here each stroke IS the chord of
 * the disc on its line, with butt caps, so the stroke batch draws the same
 * picture with no clip and no program of its own. A stroke whose line only
 * touches the rim has no chord and draws nothing.
 */
function hatchShimmer(c: HoldFireCtx): void {
  const r = c.row;
  const n = Math.max(1, r.count);
  const disc = r.rPx;
  const band = frac(c.t / r.periodSec) * 2 * r.r2Px - r.r2Px;
  const spread = 2 * r.length2Px * r.length2Px;
  for (let k = 0; k < n; k++) {
    // `u` runs across the hatch, from one side of the disc to the other.
    const u = (k - (n - 1) / 2) * r.lengthPx;
    const h2 = disc * disc - u * u;
    if (h2 <= 0) continue;
    const h = Math.sqrt(h2);
    const du = u - band;
    const a = r.alpha + r.alpha2 * Math.exp(-(du * du) / spread);
    const mx = c.x + u * HATCH_COS, my = c.y + u * HATCH_SIN;
    line(c, mx + h * HATCH_SIN, my - h * HATCH_COS, mx - h * HATCH_SIN, my + h * HATCH_COS,
      r.widthPx, a, false);
  }
  ring(c, disc, r.widthPx, r.alpha3);
}

/** The built looks of this file, by id. */
export const RECIPES: Readonly<Record<string, HoldFireRecipe>> = {
  'safety-lock': safetyLock,
  'safed-guns': safedGuns,
  'restricted-line': restrictedLine,
  clamp,
  'hatch-shimmer': hatchShimmer,
};

/** The looks still to build. They draw the AUTO look. */
export const PENDING: readonly string[] = [];
